// Google Analytics 4 Tracking Utilities
// To enable GA4 tracking, add your Measurement ID to .env:
// VITE_GA4_MEASUREMENT_ID=G-XXXXXXXXXX

declare global {
  interface Window {
    gtag?: (
      command: "config" | "event" | "js",
      targetId: string | Date,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

export const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;

// Check if GA4 is enabled
export const isGA4Enabled = (): boolean => {
  return !!GA4_MEASUREMENT_ID && GA4_MEASUREMENT_ID !== "undefined";
};

// Initialize GA4
export const initGA4 = (): void => {
  if (!isGA4Enabled()) {
    // GA4 not configured - silently skip initialization in production
    if (import.meta.env.DEV) {
      console.warn(
        "GA4: Measurement ID not configured. Add VITE_GA4_MEASUREMENT_ID to your .env file."
      );
    }
    return;
  }

  // Create script tag for gtag.js
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer?.push(args);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA4_MEASUREMENT_ID, {
    send_page_view: false, // We'll handle page views manually
  });

  if (import.meta.env.DEV) {
    console.info("GA4: Initialized with ID:", GA4_MEASUREMENT_ID);
  }
};

// Track page views
export const trackPageView = (path: string, title?: string): void => {
  if (!isGA4Enabled() || !window.gtag) return;

  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title || document.title,
  });
};

// Track custom events
export const trackEvent = (eventName: string, eventParams?: Record<string, unknown>): void => {
  if (!isGA4Enabled() || !window.gtag) return;

  window.gtag("event", eventName, eventParams);
};

// Predefined conversion events
export const trackFormSubmission = (formName: string, formType: string): void => {
  trackEvent("form_submission", {
    form_name: formName,
    form_type: formType,
  });
};

export const trackPhoneClick = (location: string): void => {
  trackEvent("phone_click", {
    location,
    phone_number: "800.905.6419",
  });
};

export const trackButtonClick = (buttonName: string, destination?: string): void => {
  trackEvent("button_click", {
    button_name: buttonName,
    destination,
  });
};

export const trackInsuranceVerification = (): void => {
  trackEvent("insurance_verification_started", {
    conversion: true,
  });
};

export const trackContactFormStart = (): void => {
  trackEvent("contact_form_started");
};

export const trackApplicationStart = (applicationType: string): void => {
  trackEvent("application_started", {
    application_type: applicationType,
  });
};

// Track conversions (form completions)
export const trackConversion = (conversionType: string, value?: number): void => {
  trackEvent("conversion", {
    conversion_type: conversionType,
    value: value || 1,
  });
};

// Track chat events
export const trackChatEvent = (action: string, data?: Record<string, unknown>): void => {
  trackEvent("chat", {
    action,
    ...data,
  });
};
